
import React, { useEffect, useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { CheckCircle, ChevronRight, ChevronLeft, Maximize2, X, Play, ChevronDown, ChevronUp } from 'lucide-react';
import { SEO } from '../components/SEO';
import { SERVICES, GALLERY_IMAGES, COMMON_FAQS } from '../constants';
import ContactForm from '../components/ContactForm';
import { GalleryImage } from '../types';
import RevealOnScroll from '../components/RevealOnScroll';

const ServiceDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const service = SERVICES.find(s => s.id === id);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  const [currentImgIndex, setCurrentImgIndex] = useState(0);
  const [lightboxItem, setLightboxItem] = useState<GalleryImage | null>(null);

  // Robust filtering that handles the case where service might be undefined
  const relevantImages = service ? GALLERY_IMAGES.filter(img => {
    const serviceTitle = service.title.toLowerCase();
    const imgCategory = img.category.toLowerCase();
    // Match full category or the first word (e.g., "Wedding" matches "Wedding & Bridal")
    const categoryMatch = serviceTitle.includes(imgCategory) ||
      imgCategory.includes(serviceTitle.split(' ')[0] || '');
    return categoryMatch;
  }).slice(0, 6) : [];

  const displayImages = relevantImages.length > 0 ? relevantImages : GALLERY_IMAGES.slice(0, 5);

  useEffect(() => {
    window.scrollTo(0, 0);
    setCurrentImgIndex(0);
  }, [id]);

  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  const nextImg = () => {
    setCurrentImgIndex((prev) => (prev + 1) % displayImages.length);
  };

  const prevImg = () => {
    setCurrentImgIndex((prev) => (prev - 1 + displayImages.length) % displayImages.length);
  };

  // Safe guard: Redirect if service not found, but AFTER hooks are declared
  if (!service) {
    return <Navigate to="/" />;
  }

  return (
    <>
      <SEO
        title={`${service.title} Services in Midnapore`}
        description={`Looking for ${service.title}? We provide the best ${service.title.toLowerCase()} in Midnapore, Kharagpur & Ghatal. Custom themes & affordable prices.`}
        url={`/services/${service.id}`}
        image={service.imageUrl}
      />

      <div className="relative bg-maroon-900 py-20 md:py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img src={service.imageUrl} alt="" className="w-full h-full object-cover opacity-20 blur-sm scale-110" />
          <div className="absolute inset-0 bg-gradient-to-t from-maroon-900 via-maroon-900/80 to-maroon-900/50"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10 text-center">
          <nav className="flex justify-center items-center text-sm mb-6 animate-fade-in" aria-label="Breadcrumb">
            <Link to="/" className="text-gold-400 hover:text-white transition-colors uppercase tracking-widest font-bold text-xs">Home</Link>
            <ChevronRight className="w-4 h-4 mx-2 text-gray-500" />
            <span className="text-white uppercase tracking-widest font-bold text-xs">{service.title}</span>
          </nav>

          <h1 className="text-4xl md:text-7xl font-serif font-bold text-white mb-6 animate-hero-fade-up">{service.title}</h1>
          <div className="w-32 h-1.5 bg-gradient-to-r from-gold-300 to-gold-600 mx-auto rounded-full"></div>
        </div>
      </div>

      <div className="bg-cream-50 min-h-screen py-12 md:py-20">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-3 gap-8 md:gap-12">
            <div className="lg:col-span-2 space-y-12">
              <div className="space-y-8">
                <div className="rounded-2xl overflow-hidden shadow-2xl border-4 border-white group relative">
                  {/* Updated Image Styling for better coverage */}
                  <img
                    src={service.imageUrl}
                    alt={service.title}
                    className="w-full h-auto max-h-[600px] object-cover"
                  />
                </div>

                <RevealOnScroll className="bg-white p-6 md:p-10 rounded-2xl shadow-sm border border-gray-100" effect="fade-up">
                  <h2 className="text-2xl md:text-4xl font-serif text-maroon-900 font-bold mb-6">Overview</h2>
                  <p className="text-gray-600 leading-8 text-base md:text-lg font-light">{service.fullDescription}</p>

                  <h3 className="text-xl md:text-3xl font-serif text-maroon-900 font-bold mt-12 mb-8 border-l-4 border-gold-500 pl-6">Service Highlights</h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {service.features.map((feature, index) => (
                      <div key={index} className="flex items-center bg-gray-50 p-4 rounded-xl border border-gray-100">
                        <CheckCircle className="w-6 h-6 text-gold-600 mr-4 flex-shrink-0" />
                        <span className="font-semibold text-gray-700 text-sm md:text-base">{feature}</span>
                      </div>
                    ))}
                  </div>
                </RevealOnScroll>
              </div>

              <div className="bg-white p-6 md:p-10 rounded-2xl shadow-sm border border-gray-100">
                <div className="flex items-center justify-between mb-8">
                  <h3 className="text-xl md:text-3xl font-serif text-maroon-900 font-bold border-l-4 border-gold-500 pl-6">Work Showcase</h3>
                  <div className="flex items-center gap-2">
                    <button onClick={prevImg} className="p-2 rounded-full border border-gray-200 hover:bg-gold-500 hover:text-white transition-all text-gray-400">
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <button onClick={nextImg} className="p-2 rounded-full border border-gray-200 hover:bg-gold-500 hover:text-white transition-all text-gray-400">
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                <div className="relative group overflow-hidden rounded-xl shadow-lg aspect-video bg-gray-900">
                  {displayImages.map((img, idx) => (
                    <div key={img.id} className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${idx === currentImgIndex ? 'opacity-100 z-10' : 'opacity-0 pointer-events-none'}`}>
                      {img.type === 'video' ? (
                        <div className="w-full h-full relative">
                          <video
                            src={img.url}
                            className="w-full h-full object-cover"
                            muted
                            loop
                            autoPlay
                            playsInline
                          />
                          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                            <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center border border-white/30">
                              <Play className="w-5 h-5 text-white fill-current ml-0.5" />
                            </div>
                          </div>
                        </div>
                      ) : (
                        <img src={img.url} alt={img.alt} className="w-full h-full object-cover" />
                      )}

                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
                      <button onClick={() => setLightboxItem(img)} className="absolute top-6 right-6 bg-white/20 backdrop-blur-md p-3 rounded-full text-white opacity-0 group-hover:opacity-100 transition-all hover:bg-gold-500 pointer-events-auto z-20">
                        <Maximize2 className="w-5 h-5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* FAQ Section */}
              <RevealOnScroll className="bg-white p-6 md:p-10 rounded-2xl shadow-sm border border-gray-100" effect="fade-up">
                <h3 className="text-xl md:text-3xl font-serif text-maroon-900 font-bold mb-6 border-l-4 border-gold-500 pl-6">Frequently Asked Questions</h3>
                <div className="space-y-4">
                  {COMMON_FAQS.map((faq, index) => (
                    <div key={index} className="border border-gray-100 rounded-lg overflow-hidden">
                      <button
                        onClick={() => toggleFaq(index)}
                        className="w-full flex items-center justify-between p-4 bg-gray-50 text-left hover:bg-gray-100 transition-colors"
                      >
                        <span className="font-semibold text-gray-700">{faq.question}</span>
                        {openFaqIndex === index ? (
                          <ChevronUp className="w-5 h-5 text-gold-600" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-400" />
                        )}
                      </button>
                      <div
                        className={`transition-all duration-300 ease-in-out overflow-hidden ${openFaqIndex === index ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'
                          }`}
                      >
                        <div className="p-4 text-gray-600 bg-white border-t border-gray-100 leading-relaxed text-sm md:text-base">
                          {faq.answer}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </RevealOnScroll>
            </div>

            <div className="lg:col-span-1">
              <div className="sticky top-28 space-y-8">
                <ContactForm />
                <div className="bg-white p-8 rounded-2xl shadow-xl border-t-8 border-maroon-900">
                  <h4 className="font-serif font-bold text-2xl mb-6 text-maroon-900">Other Services</h4>
                  <ul className="space-y-3">
                    {SERVICES.filter(s => s.id !== service.id).map(s => (
                      <li key={s.id}>
                        <Link to={`/services/${s.id}`} className="block p-4 bg-gray-50 rounded-lg hover:bg-gold-500 hover:text-white transition-all duration-300 font-medium flex items-center justify-between text-sm">
                          {s.title}
                          <ChevronRight className="w-4 h-4" />
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div >

      {lightboxItem && (
        <div className="fixed inset-0 z-[60] bg-black/95 flex items-center justify-center p-4 animate-fade-in" onClick={() => setLightboxItem(null)}>
          <button className="absolute top-6 right-6 text-white/70 hover:text-white transition-colors bg-white/10 p-2 rounded-full z-50">
            <X className="w-8 h-8" />
          </button>

          <div className="relative max-w-full max-h-full flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
            {lightboxItem.type === 'video' ? (
              <video
                src={lightboxItem.url}
                controls
                autoPlay
                className="max-w-[95vw] max-h-[85vh] shadow-2xl border-4 border-white/10 rounded-sm focus:outline-none"
              />
            ) : (
              <img
                src={lightboxItem.url}
                alt={lightboxItem.alt}
                className="max-w-full max-h-[85vh] rounded-sm shadow-2xl border-4 border-white"
              />
            )}
          </div>
        </div>
      )
      }
    </>
  );
};

export default ServiceDetail;
